// SECURITY: Enhanced storage utilities with input validation
const MAX_KEY_LENGTH = 200;
const MAX_VALUE_SIZE = 5 * 1024 * 1024; // 5MB max value size

// SECURITY: Validate storage key to prevent injection attacks
function isValidStorageKey(key: string): boolean {
  if (!key || typeof key !== 'string') return false;
  if (key.length === 0 || key.length > MAX_KEY_LENGTH) return false;
  // Prevent dangerous patterns
  if (key.includes('..') || key.includes('/') || key.includes('\\')) return false;
  // Only allow alphanumeric, hyphens, underscores, and dots
  return /^[a-zA-Z0-9._-]+$/.test(key);
}

// SECURITY: Validate value size to prevent DoS via localStorage
function isValidValueSize(value: any): boolean {
  try {
    const serialized = JSON.stringify(value);
    return serialized.length <= MAX_VALUE_SIZE;
  } catch {
    return false;
  }
}

export const safeGet = <T>(k: string, fallback: T): T => {
  // SECURITY: Validate key before accessing localStorage
  if (!isValidStorageKey(k)) {
    return fallback;
  }
  
  try {
    const v = localStorage.getItem(k);
    if (!v) return fallback;
    
    // SECURITY: Validate value size before parsing
    if (v.length > MAX_VALUE_SIZE) {
      return fallback;
    }
    
    return JSON.parse(v) as T;
  } catch {
    return fallback;
  }
};

export const safeSet = (k: string, v: any) => {
  // SECURITY: Validate key and value before storing
  if (!isValidStorageKey(k)) {
    return; // Silently fail if key is invalid
  }
  
  // SECURITY: Validate value size to prevent DoS
  if (!isValidValueSize(v)) {
    return; // Silently fail if value is too large
  }
  
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    // Silently fail on storage errors (quota exceeded, etc.)
  }
};



